import React, { useMemo, useState } from 'react'
import { Paper, TextField, Typography } from '@material-ui/core'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import TextFieldsIcon from '@material-ui/icons/TextFields'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import ColorLensIcon from '@material-ui/icons/ColorLens'
import LinearScaleIcon from '@material-ui/icons/LinearScale'
import { buildPalette } from './fieldGroups'

const ACTIVE_BLUE = '#1976d2'
const PLACED_GREEN = '#2e7d32'

const TYPE_ICONS = { Text: TextFieldsIcon, Image: ImageOutlinedIcon, ColorBox: ColorLensIcon, Bar: LinearScaleIcon }

const buttonBase = {
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  height: 28,
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const stateStyle = (placed, active, selected) => {
  if (active) return { border: `1px solid ${ACTIVE_BLUE}`, background: ACTIVE_BLUE, color: '#fff' }
  if (selected) return { border: `1px solid ${ACTIVE_BLUE}`, boxShadow: `0 0 0 1px ${ACTIVE_BLUE}`, background: '#e3f2fd', color: '#0d47a1' }
  if (placed) return { border: '1px solid #c8e6c9', background: '#e8f5e9', color: PLACED_GREEN }
  return { border: '1px solid #d0d0d0', background: '#fff', color: 'rgba(0, 0, 0, 0.87)' }
}

const headingStyle = { fontWeight: 500, display: 'block' }

// Remove badge on a grid cell's top-right corner; cells are too small for an inline one.
const cornerRemove = {
  position: 'absolute', top: -6, right: -6, zIndex: 1, width: 16, height: 16, padding: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8,
  border: '1px solid #bdbdbd', background: '#fff', color: '#616161', cursor: 'pointer',
}

const bareButton = {
  display: 'inline-flex', alignItems: 'center', gap: 4, height: '100%', padding: 0,
  border: 0, background: 'transparent', color: 'inherit', font: 'inherit', cursor: 'pointer',
}

export default function FieldPalette({
  fields, placedIds, activeKey, selectedKeys = [], disabled, disabledReason, search, onSearchChange, onPick, onSelectMany, onRemove, height, children,
}) {
  const palette = useMemo(() => buildPalette(fields), [fields])
  const [side, setSide] = useState('home')
  const [hoverKey, setHoverKey] = useState(null)
  const query = search.trim().toLowerCase()

  const hit = (text) => !query || text.toLowerCase().includes(query)
  const isPlaced = (f) => placedIds.has(f.key)
  const placedCount = fields.filter(isPlaced).length

  const shown = palette.scopes.filter((s) => !palette.hasSwitch || (s.id !== 'home' && s.id !== 'away') || s.id === side)
  const scopeFields = (s) => [...s.chips.map((c) => c.field), ...s.grids.flatMap((g) => g.rows.flatMap((r) => r.cells.filter(Boolean).map((c) => c.field)))]
  const sideMeta = (id) => {
    const scope = palette.scopes.find((s) => s.id === id)
    const list = scopeFields(scope)
    if (!query) return `${list.filter(isPlaced).length}/${list.length}`
    const found = scope.chips.filter((c) => hit(`${scope.title} ${c.label}`)).length
      + scope.grids.reduce((n, g) => n + g.rows.reduce((m, r) => m + r.cells.filter((c) => c && hit(c.title)).length, 0), 0)
    return `${found} found`
  }
  const anyMatch = palette.scopes.some((s) => s.chips.some((c) => hit(`${s.title} ${c.label}`))
    || s.grids.some((g) => g.rows.some((r) => r.cells.some((c) => c && hit(c.title)))))

  // Shift/Ctrl/Cmd-click adds a placed field to the selection instead of replacing it.
  const pick = (field, e) => {
    if (!disabled) onPick(field, { additive: !!(e && (e.shiftKey || e.metaKey || e.ctrlKey)) })
  }

  // A grid header or row label selects every placed field along it.
  const selectionLabel = (label, list, title, style) => {
    const canSelect = !disabled && list.some(isPlaced)
    if (!canSelect) return <Typography variant='caption' color='textSecondary' style={style}>{label}</Typography>
    return (
      <button
        type='button'
        title={title}
        aria-label={title}
        onClick={() => onSelectMany(list)}
        style={{ ...bareButton, ...style, fontSize: 12, color: 'rgba(0, 0, 0, 0.54)', cursor: 'pointer' }}
      >
        {label}
      </button>
    )
  }

  return (
    <Paper
      square
      elevation={0}
      style={{ width: palette.wide ? 320 : 280, borderRight: '1px solid #e0e0e0', overflowY: 'auto', flexShrink: 0, ...(height && { height }) }}
    >
      <div style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff', borderBottom: '1px solid #eee', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {disabledReason && <Typography variant='caption' color='textSecondary'>{disabledReason}</Typography>}
        {children}
        <TextField
          placeholder='Filter fields'
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          size='small'
          variant='outlined'
          fullWidth
          inputProps={{ 'aria-label': 'Filter fields' }}
          InputProps={{ style: { fontSize: 13 } }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#eee', overflow: 'hidden' }}>
            <div style={{ height: 4, background: PLACED_GREEN, width: `${fields.length ? (placedCount / fields.length) * 100 : 0}%` }} />
          </div>
          <Typography variant='caption' color='textSecondary'>{placedCount} of {fields.length} placed</Typography>
        </div>
        {palette.hasSwitch && (
          <ToggleButtonGroup value={side} exclusive size='small' onChange={(_, v) => { if (v) setSide(v) }} style={{ width: '100%' }}>
            {['home', 'away'].map((id) => (
              <ToggleButton key={id} value={id} style={{ flex: 1, fontSize: 12, padding: '4px 8px', textTransform: 'none' }}>
                {id === 'home' ? 'Home team' : 'Away team'}
                <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>{sideMeta(id)}</span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </div>

      <div style={{ padding: '0 12px 16px' }}>
        {shown.map((scope) => (
          <div key={scope.id}>
            {scope.chips.length > 0 && (
              <>
                <Typography variant='caption' color='textSecondary' style={{ ...headingStyle, padding: '14px 0 6px' }}>{scope.title}</Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {scope.chips.map(({ field, label }) => {
                    const Icon = TYPE_ICONS[field.dataType] ?? TextFieldsIcon
                    const active = activeKey === field.key
                    const selected = selectedKeys.includes(field.key)
                    const placed = isPlaced(field)
                    const dim = !hit(`${scope.title} ${label}`)
                    const showRemove = placed && !disabled && (hoverKey === field.key || selected)
                    return (
                      <span
                        key={field.key}
                        onMouseEnter={() => setHoverKey(field.key)}
                        onMouseLeave={() => setHoverKey(null)}
                        onFocus={() => setHoverKey(field.key)}
                        onBlur={() => setHoverKey(null)}
                        style={{ ...buttonBase, padding: placed ? '0 4px 0 10px' : '0 10px', borderRadius: 14, ...stateStyle(placed, active, selected), opacity: dim ? 0.3 : 1 }}
                      >
                        <button
                          type='button'
                          disabled={disabled}
                          aria-pressed={active || selected}
                          onClick={(e) => pick(field, e)}
                          style={{ ...bareButton, cursor: disabled ? 'default' : 'pointer' }}
                        >
                          <Icon style={{ fontSize: 14 }} />
                          {label}
                        </button>
                        {placed && (showRemove ? (
                          <button
                            type='button'
                            aria-label={`Remove ${label}`}
                            title='Remove from canvas'
                            onClick={() => onRemove(field)}
                            style={{ ...bareButton, width: 18, height: 18, borderRadius: 9, justifyContent: 'center' }}
                          >
                            <CloseIcon style={{ fontSize: 14 }} />
                          </button>
                        ) : (
                          <span style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {!active && <CheckIcon style={{ fontSize: 12 }} titleAccess='Placed' />}
                          </span>
                        ))}
                      </span>
                    )
                  })}
                </div>
              </>
            )}
            {scope.grids.map((grid) => {
              const players = grid.id === 'players'
              const template = players
                ? `40px repeat(${grid.columns.length}, minmax(0, 1fr))`
                : `minmax(0, 1fr) repeat(${grid.columns.length}, 48px)`
              return (
                <div key={grid.id}>
                  <Typography variant='caption' color='textSecondary' style={{ ...headingStyle, padding: '16px 0 6px' }}>
                    {scope.chips.length > 0 ? grid.title : `${scope.title} · ${grid.title.toLowerCase()}`}
                  </Typography>
                  <div style={{ display: 'grid', gridTemplateColumns: template, gap: '4px 6px', alignItems: 'center' }}>
                    <Typography variant='caption' color='textSecondary'>{grid.corner}</Typography>
                    {grid.columns.map((c, i) => (
                      <React.Fragment key={c.id}>
                        {selectionLabel(
                          c.label,
                          grid.rows.map((row) => row.cells[i]?.field).filter(Boolean),
                          `Select all placed ${scope.title.toLowerCase()} ${c.name.toLowerCase()} fields`,
                          { justifyContent: 'center' },
                        )}
                      </React.Fragment>
                    ))}
                    {grid.rows.map((row) => (
                      <React.Fragment key={row.id}>
                        {selectionLabel(
                          row.label,
                          row.cells.filter(Boolean).map((c) => c.field),
                          `Select all placed ${scope.title.toLowerCase()} ${grid.id === 'players' ? `player ${row.label}` : row.label} fields`,
                          { justifyContent: 'flex-start', fontSize: 13 },
                        )}
                        {row.cells.map((cell, i) => {
                          if (!cell) return <span key={grid.columns[i].id} style={{ textAlign: 'center', color: '#c4c4c4', fontSize: 12 }}>–</span>
                          const active = activeKey === cell.field.key
                          const selected = selectedKeys.includes(cell.field.key)
                          const placed = isPlaced(cell.field)
                          const showRemove = placed && !disabled && (hoverKey === cell.field.key || selected)
                          return (
                            <span
                              key={grid.columns[i].id}
                              onMouseEnter={() => setHoverKey(cell.field.key)}
                              onMouseLeave={() => setHoverKey(null)}
                              onFocus={() => setHoverKey(cell.field.key)}
                              onBlur={() => setHoverKey(null)}
                              style={{ position: 'relative', display: 'flex', minWidth: 0 }}
                            >
                              <button
                                type='button'
                                title={cell.title}
                                aria-label={cell.title}
                                disabled={disabled}
                                aria-pressed={active || selected}
                                onClick={(e) => pick(cell.field, e)}
                                style={{ ...buttonBase, flex: 1, minWidth: 0, padding: 0, borderRadius: 6, ...stateStyle(placed, active, selected), opacity: hit(cell.title) ? 1 : 0.3 }}
                              >
                                {placed && !active && <CheckIcon style={{ fontSize: 12 }} />}
                                {!players && grid.columns[i].label}
                              </button>
                              {showRemove && (
                                <button
                                  type='button'
                                  title='Remove from canvas'
                                  aria-label={`Remove ${cell.title}`}
                                  onClick={() => onRemove(cell.field)}
                                  style={{ ...cornerRemove }}
                                >
                                  <CloseIcon style={{ fontSize: 11 }} />
                                </button>
                              )}
                            </span>
                          )
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
        {query && !anyMatch && (
          <Typography variant='body2' color='textSecondary' style={{ padding: '16px 0' }}>No fields match that filter.</Typography>
        )}
      </div>
    </Paper>
  )
}
